import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Mpox (Monkeypox) Vaccine Study
 * Target population: Healthy adults aged 18-49 with BMI 18.5-35
 * Trial ID: VMPOX046
 */
export const mpoxVaccineScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49"
      },
      {
        value: "50_to_64",
        label: "50 to 64",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is for adults aged 18 to 49.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 18.5,
    maxBMI: 35,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "mpox_history",
    type: QuestionType.RADIO,
    question: "Have you ever had monkeypox (mpox), received an mpox vaccine, or had close contact with someone with mpox?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had mpox, received an mpox vaccine, or had close contact",
        disqualify: true
      },
      {
        value: "no",
        label: "No, none of these apply to me"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with mpox history, vaccination, or recent exposure cannot participate.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },

  {
    id: "health_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following health conditions?",
    helpText: "This study is for generally healthy adults. Please select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "autoimmune",
        label: "Autoimmune disease (such as lupus, rheumatoid arthritis, Crohn's disease)",
        disqualify: true
      },
      {
        value: "immunocompromised",
        label: "Immunocompromised or take immunosuppressive medications",
        disqualify: true
      },
      {
        value: "cardiovascular",
        label: "Cardiovascular disease or heart condition",
        disqualify: true
      },
      {
        value: "liver_cirrhosis",
        label: "Liver failure or cirrhosis",
        disqualify: true
      },
      {
        value: "kidney_dialysis",
        label: "End stage kidney disease or dialysis",
        disqualify: true
      },
      {
        value: "cancer_history",
        label: "History of cancer",
        disqualify: true
      },
      {
        value: "organ_transplant",
        label: "Organ transplant (received or on waitlist)",
        disqualify: true
      },
      {
        value: "guillain_barre",
        label: "History of Guillain-Barré syndrome",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above - I am generally healthy"
      },
    ],
    disqualifyMessage: "This study is for generally healthy adults without these chronic conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        autoimmune: [
          { attributeDefinitionId: 23135, booleanAttributeValue: true },
        ],
        immunocompromised: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true },
        ],
        cardiovascular: [
          { attributeDefinitionId: 23018, booleanAttributeValue: true }, // has_cardiovasculardisease
        ],
        liver_cirrhosis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
        ],
        kidney_dialysis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
        cancer_history: [
          { attributeDefinitionId: 23423, booleanAttributeValue: true }, // has_haematological_malignancy (use as proxy for cancer history)
        ],
        organ_transplant: [
          { attributeDefinitionId: 6300, booleanAttributeValue: true },
        ],
        guillain_barre: [
          { attributeDefinitionId: 25742, booleanAttributeValue: true },
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "vaccine_anaphylaxis",
    type: QuestionType.RADIO,
    question: "Have you ever had a severe allergic reaction (anaphylaxis) to any vaccine?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had anaphylaxis to a vaccine",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had anaphylaxis to a vaccine"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of anaphylaxis to vaccines cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23486, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 23486, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_treatments",
    type: QuestionType.CHECKBOX,
    question: "Have you received any of the following in the past 2-3 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "blood_products",
        label: "Blood transfusions or blood products (past 2 months)",
        disqualify: true
      },
      {
        value: "ivig",
        label: "IVIg (intravenous immunoglobulin) - currently taking",
        disqualify: true
      },
      {
        value: "mental_health_hospitalization",
        label: "Hospitalized for a mental health condition (past 3 months)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received these treatments recently cannot participate.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        blood_products: [
          { attributeDefinitionId: 23724, booleanAttributeValue: true }, // had_blood_products_3_months (close enough for 2 months)
        ],
        ivig: [
          { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        mental_health_hospitalization: [],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "pregnancy_breastfeeding",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently pregnant or breastfeeding"
      },
      {
        value: "not_applicable",
        label: "Not applicable (I am male or cannot become pregnant)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who are pregnant or breastfeeding cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26453, booleanAttributeValue: true },
        { attributeDefinitionId: 26455, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26453, booleanAttributeValue: false },
        { attributeDefinitionId: 26455, booleanAttributeValue: false },
      ],
      not_applicable: [],
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
